import { Dispatch } from 'redux';
import { Commit } from '../../models';
import { Commits } from '../../services';
import { createRestSlices, applyReducers } from './rest';
import { ActionPayload } from '..';
import type { Status, Callback, Response } from './rest';
import { removeFromList } from './remoteUpdate';

const {
    initialState,
    getReducer, listReducer, deleteReducer,
    getAction, listAction, deleteAction,
} = createRestSlices(Commits);

const LINKING_COMMIT           = 'yoda/commits/LINKING_COMMIT';
const LINKING_COMMIT_SUCCESS   = 'yoda/commits/LINKING_COMMIT_SUCCESS';
const LINKING_COMMIT_FAILURE   = 'yoda/commits/LINKING_COMMIT_FAILURE';
const LINKING_COMMIT_RESET     = 'yoda/commits/LINKING_COMMIT_RESET';
const UNLINKING_COMMIT         = 'yoda/commits/UNLINKING_COMMIT';
const UNLINKING_COMMIT_SUCCESS = 'yoda/commits/UNLINKING_COMMIT_SUCCESS';
const UNLINKING_COMMIT_FAILURE = 'yoda/commits/UNLINKING_COMMIT_FAILURE';

initialState.linkingCommit        = 'idle' as Status;
initialState.linkingCommitId      = null;
initialState.linkingCommitError   = null;
initialState.unlinkingCommit      = 'idle' as Status;
initialState.unlinkingCommitId    = null;
initialState.unlinkingCommitError = null;

const UPDATE = 'yoda/mercure/REMOTE_UPDATE';
const onRemoteUpdateReducer = (state = initialState, action: ActionPayload) => {
    if (action.type !== UPDATE)
        return state;

    const { update: { data, type }} = action;
    if (type !== 'commit.deleted')
        return state;

    return {
        ...state,
        commits: removeFromList(state.commits, data._id),
        commit: state.commit?._id === data._id ? null : state.commit
    };
};

const linkCommitReducer = (state = initialState, action: ActionPayload) => {
    let commits;
    switch (action.type) {
        case LINKING_COMMIT:
            return {
                ...state,
                linkingCommit: 'pending',
                linkCommitId: action.id,
                linkCommitError: null
            };
        case LINKING_COMMIT_SUCCESS:
            commits = state.commits.map((commit: Commit) => {
                if (commit._id === action.commit._id)
                    return action.commit;
                return commit;
            });
            return {
                ...state,
                linkingCommit: 'succeeded',
                linkCommitError: null,
                commits,
            };
        case LINKING_COMMIT_FAILURE:
            return {
                ...state,
                linkingCommit: 'failed',
                linkCommitError: action.error
            };
        case UNLINKING_COMMIT:
            return {
                ...state,
                unlinkingCommit: 'pending',
                unlinkCommitId: action.id,
                unlinkCommitError: null
            };
        case UNLINKING_COMMIT_SUCCESS:
            commits = state.commits.map((commit: Commit) => {
                if (commit._id === action.commit._id)
                    return action.commit;
                return commit;
            });
            return {
                ...state,
                unlinkingCommit: 'succeeded',
                unlinkCommitError: null,
                commits,
            };
        case UNLINKING_COMMIT_FAILURE:
            return {
                ...state,
                unlinkingCommit: 'failed',
                unlinkCommitError: action.error
            };
        case LINKING_COMMIT_RESET:
            return {
                ...state,
                linkingCommit: 'idle',
                linkCommitId: null,
                linkCommitError: null,
                unlinkingCommit: 'idle',
                unlinkCommitId: null,
                unlinkCommitError: null
            };
        default:
            return state;
    }
};

const linkCommitAction = (commitId: string, changeId?: string, callback?: Callback) => {
    return (dispatch: Dispatch) => {
        dispatch({type: LINKING_COMMIT, id: commitId});
        return Commits.linkToChange(commitId, changeId)
            .then((data: Response) => {
                const {commit} = data;
                dispatch({type: LINKING_COMMIT_SUCCESS, commit});
                setTimeout(() => {
                    dispatch({type: LINKING_COMMIT_RESET});
                }, 3000);
                callback && callback(/*err*/null);
            })
            .catch((error: Error) => {
                dispatch({type: LINKING_COMMIT_FAILURE, error: error.message});
                setTimeout(() => {
                    dispatch({type: LINKING_COMMIT_RESET});
                }, 3000);
                callback && callback(error);
            });
    }
};

const unlinkCommitAction = (commitId: string, callback?: Callback) => {
    return (dispatch: Dispatch) => {
        dispatch({type: UNLINKING_COMMIT, id: commitId});
        return Commits.unlink(commitId)
            .then((data: Response) => {
                const {commit} = data;
                dispatch({type: UNLINKING_COMMIT_SUCCESS, commit});
                setTimeout(() => {
                    dispatch({type: LINKING_COMMIT_RESET});
                }, 3000);
                callback && callback(/*err*/null);
            })
            .catch((error: Error) => {
                dispatch({type: UNLINKING_COMMIT_FAILURE, error: error.message});
                setTimeout(() => {
                    dispatch({type: LINKING_COMMIT_RESET});
                }, 3000);
                callback && callback(error);
            });
    }
};

/* Export reducer */
/* eslint import/no-anonymous-default-export: [2, {"allowArrowFunction": true}] */
export default (state = initialState, action: ActionPayload) => {
    return applyReducers(state, action, [
        getReducer, listReducer, deleteReducer, linkCommitReducer, onRemoteUpdateReducer
    ]);
}

/* Export CRUD actions */
export const loadCommit   = getAction;
export const loadCommits  = listAction;
export const deleteCommit = deleteAction;
export const linkCommit   = linkCommitAction;
export const unlinkCommit = unlinkCommitAction;
